import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { imageSrcSet, imageUrl } from './assets';
import { CAPABILITIES } from './content';
import { useInteriorLandingT, useStartInterior } from './kit';
import type { InteriorLandingPage } from './types';

/**
 * Every way to change a room, each shown with a real result. A tile opens the
 * designer in that mode; staging and paint also link to their own pages.
 */
export default function Capabilities({ page }: { page: InteriorLandingPage }) {
  const { t } = useInteriorLandingT();
  const start = useStartInterior(page);
  const reveal = useReveal<HTMLDivElement>(0.1);

  return (
    <section id='what-you-can-do' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
        <SectionHeading
          title={t('One photo,')}
          tail={t('every kind of change.')}
          lead={t(
            'Redesign the whole room or change one detail. Each tool keeps the shape of your room, so the result still looks like your space.',
          )}
        />

        <div
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-14 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3',
          )}
        >
          {CAPABILITIES.map((item, index) => (
            <article
              key={item.mode}
              className={clsxm(styles.rv, 'group flex flex-col')}
              style={delay((index % 3) * 90)}
            >
              <button
                type='button'
                onClick={() => start({ surface: 'mode', mode: item.mode })}
                aria-label={t('Try it: {{tool}}', { tool: t(item.title) })}
                className='relative block w-full overflow-hidden rounded-[22px] bg-[#e9e9f2] ring-1 ring-[#0f0f28]/[0.07] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:bg-[#1b1b28] dark:ring-white/10'
                style={{ aspectRatio: '4 / 3' }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={imageUrl(item.result, 640)}
                  srcSet={imageSrcSet(item.result)}
                  sizes='(min-width: 1024px) 400px, (min-width: 640px) 46vw, 92vw'
                  alt={t(item.title)}
                  loading='lazy'
                  decoding='async'
                  className='absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]'
                />
                {item.before ? (
                  <span className='absolute bottom-3 start-3 w-[30%] overflow-hidden rounded-xl shadow-[0_10px_24px_-10px_rgba(15,15,40,0.6)] ring-2 ring-white'>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageUrl(item.before, 320)}
                      alt=''
                      loading='lazy'
                      decoding='async'
                      className='block aspect-[4/3] w-full object-cover'
                    />
                    <span className='absolute start-1.5 top-1.5 rounded-full bg-[#0b0b13]/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white'>
                      {t('Before')}
                    </span>
                  </span>
                ) : null}
                {item.ask ? (
                  <span className='absolute end-3 top-3 max-w-[78%] rounded-2xl rounded-se-md bg-white/95 px-3 py-2 text-start text-[13px] font-medium leading-snug text-[#10101c] shadow-[0_10px_24px_-12px_rgba(15,15,40,0.5)]'>
                    {t(item.ask)}
                  </span>
                ) : null}
              </button>
              <h3
                className={clsxm(
                  'mt-5 text-lg font-semibold tracking-tight',
                  tone.ink,
                )}
              >
                {t(item.title)}
              </h3>
              <p className={clsxm('mt-2 text-[15px] leading-7', tone.body)}>
                {t(item.body)}
              </p>
              <div className='mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold'>
                <button
                  type='button'
                  onClick={() => start({ surface: 'mode', mode: item.mode })}
                  className='inline-flex items-center gap-1.5 text-primary-600 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300'
                >
                  {t('Try it')}
                  <ArrowGlyph className='h-3.5 w-3.5' />
                </button>
                {item.link && item.link.path !== page.path ? (
                  <Link
                    href={item.link.path}
                    className={clsxm(
                      'underline decoration-[#0f0f28]/20 underline-offset-4 transition-colors hover:decoration-current dark:decoration-white/25',
                      tone.body,
                    )}
                  >
                    {t(item.link.label)}
                  </Link>
                ) : null}
              </div>
            </article>
          ))}
        </div>

        <p
          className={clsxm('mt-14 max-w-3xl text-[15px] leading-7', tone.body)}
        >
          {t(
            'Also in the designer: Declutter to tidy a messy room, 360° panoramas you can look around in, short videos of any design, and marks you can place on the photo to change exact spots.',
          )}
        </p>
      </div>
    </section>
  );
}
